import React, { useState } from "react";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Button } from "src/components/ui/Button";
import { Link } from "react-router-dom";
import { ROUTES } from "src/routes/RouteConfig";

import {
  BookOpen,
  Users,
  Target,
  CheckCircle,
  ArrowRight,
  Terminal,
  Code2,
  Wrench,
  ChevronRight,
  Sparkles
} from "lucide-react";

interface MilestoneBuild {
  title: string;
  description: string;
  skills: string[];
}

interface JavaScriptModule {
  level: number;
  grade: string;
  phase: string;
  duration: string;
  status: "available" | "coming_soon";
  description: string;
  milestone: MilestoneBuild;
  code: string;
  terminalOutput: string;
  topics: string[];
  tutorFocus: string;
}

const javascriptCurriculum: JavaScriptModule[] = [
  {
    level: 1,
    grade: "Getting Started",
    phase: "Foundations",
    duration: "Foundations (3-4 Sessions)",
    status: "available",
    description: "Set up a modern JavaScript workspace, inspect browser developer tools, and write your first interactive scripts.",
    milestone: {
      title: "Interactive Greeting & Profile Bot",
      description: "Learners code an interactive conversational script that welcomes the user, formats biographical data, calculates age milestones, and verifies runtime execution in DevTools.",
      skills: ["Console API (log, table, group)", "Template Literals", "Variables (const/let)", "Error Reading"]
    },
    code: `// Level 1: First interactive script & DevTools logging
const userName = "Alex";
const birthYear = 2010;

const currentYear = 2026;
const age = currentYear - birthYear;

console.group("🚀 rfLearn JavaScript Onboarding");
console.log(\`Welcome to rfLearn, \${userName}!\`);
console.log(\`In \${currentYear}, you will celebrate \${age} years.\`);
console.log("Status: JavaScript environment verified and ready!");
console.groupEnd();`,
    terminalOutput: `$ node greeting_bot.js
🚀 rfLearn JavaScript Onboarding
  Welcome to rfLearn, Alex!
  In 2026, you will celebrate 16 years.
  Status: JavaScript environment verified and ready!`,
    topics: [
      "What JavaScript is for and how browser engines execute code",
      "Setting up VS Code and using browser DevTools console",
      "Writing your first script and using console.log()",
      "Reading tracebacks and understanding syntax errors"
    ],
    tutorFocus: "Tutor walks through browser DevTools setup, debugs early syntax typos live, and ensures the learner understands statement execution."
  },
  {
    level: 2,
    grade: "Values and Variables",
    phase: "Foundations",
    duration: "Foundations (4 Sessions)",
    status: "available",
    description: "Store and transform information using numbers, strings, and booleans so programs can remember values and make decisions later.",
    milestone: {
      title: "Smart Tip & Currency Bill Splitter",
      description: "A practical financial calculator that parses input amounts, calculates customizable gratuity, and splits costs accurately per person with clean decimal precision.",
      skills: ["Number & String Math", "Type Casting (Number, parseFloat)", "let vs const Memory Slots", "Math.round & toFixed"]
    },
    code: `// Level 2: Financial Calculator
const billAmount = 124.50;
const tipPercentage = 18;
const partySize = 3;

const tipAmount = Number((billAmount * (tipPercentage / 100)).toFixed(2));
const grandTotal = Number((billAmount + tipAmount).toFixed(2));
const costPerPerson = Number((grandTotal / partySize).toFixed(2));

console.log("--- Payment Summary ---");
console.log(\`Tip: \\\$\${tipAmount.toFixed(2)}\`);
console.log(\`Total: \\\$\${grandTotal.toFixed(2)}\`);
console.log(\`Each person pays: \\\$\${costPerPerson.toFixed(2)}\`);`,
    terminalOutput: `$ node bill_splitter.js
Enter bill amount ($): 124.50
Tip percentage (15/18/20): 18
Number of diners: 3

--- Payment Summary ---
Tip: $22.41
Total: $146.91
Each person pays: $48.97`,
    topics: [
      "Numbers, strings, and booleans in JavaScript",
      "Variables as memory slots (let vs const)",
      "Explicit type conversion: Number(), String(), Boolean()",
      "String interpolation and input validation"
    ],
    tutorFocus: "Tutor provides mental models for computer memory and guides the student to catch implicit coercion traps and NaN bugs."
  },
  {
    level: 3,
    grade: "Control Flow",
    phase: "Core Skills",
    duration: "Core Skills (4-5 Sessions)",
    status: "available",
    description: "Direct the path of a program with conditions and loops that repeat work until a goal is met.",
    milestone: {
      title: "Logic & Binary Search Guessing Game",
      description: "A fully playable number guessing game featuring randomized secret targets, remaining attempt counters, higher/lower hints, and win/loss states.",
      skills: ["if / else if / else Branching", "while & for Loops", "Comparison & Logical (&&, ||)", "break & continue"]
    },
    code: `// Level 3: Secret Number Game
const secret = 32; // Random target between 1 and 50
let attempts = 5;
const simulatedGuesses = [25, 38, 32];

console.log("I picked a number between 1 and 50. You have 5 tries!");

for (const guess of simulatedGuesses) {
  console.log(\`Tries left [\${attempts}]. Your guess: \${guess}\`);
  
  if (guess === secret) {
    console.log("🎉 BINGO! You discovered the secret number!");
    break;
  } else if (guess < secret) {
    console.log("Too low! Aim higher.");
  } else {
    console.log("Too high! Aim lower.");
  }
  attempts--;
}

if (attempts === 0) {
  console.log(\`Out of tries! The number was \${secret}.\`);
}`,
    terminalOutput: `$ node guessing_game.js
I picked a number between 1 and 50. You have 5 tries!
Tries left [5]. Your guess: 25
Too low! Aim higher.
Tries left [4]. Your guess: 38
Too high! Aim lower.
Tries left [3]. Your guess: 32
🎉 BINGO! You discovered the secret number!`,
    topics: [
      "Branching execution with if, else if, and switch",
      "Logical operators: &&, ||, ! and strict equality ===",
      "Repetition with while loops and for iterators",
      "Loop safety and termination guarantees"
    ],
    tutorFocus: "Tutor diagrams flowchart decision trees and teaches how binary search strategy solves this in fewer attempts."
  },
  {
    level: 4,
    grade: "Functions and Scope",
    phase: "Core Skills",
    duration: "Core Skills (4 Sessions)",
    status: "available",
    description: "Break problems into named pieces of work that can be reused, tested, and combined.",
    milestone: {
      title: "Modular Trivia & Math Quiz Engine",
      description: "An extensible quiz program built with modular functions for question scoring, percentage calculation, and automated letter grade evaluation.",
      skills: ["Function Declarations & Arrow Syntax", "Parameters & Return Values", "Block vs Function Scope", "Code Modularity"]
    },
    code: `// Level 4: Modular Quiz Engine
function gradeAnswer(userAns, correctAns) {
  return userAns.trim().toLowerCase() === correctAns.toLowerCase() ? 1 : 0;
}

function calculatePercent(score, totalQuestions) {
  return Number(((score / totalQuestions) * 100).toFixed(1));
}

function assignLetter(percent) {
  if (percent >= 90) return "A";
  if (percent >= 80) return "B";
  return "C";
}

// Main quiz execution
const points = gradeAnswer("paris", "Paris") + gradeAnswer("8", "8");
const scorePct = calculatePercent(points, 2);
console.log(\`Score: \${scorePct}% | Grade: \${assignLetter(scorePct)}\`);`,
    terminalOutput: `$ node quiz_engine.js
[Q1] Capital of France? > Paris
[Q2] What is 2 ** 3? > 8

Score: 100.0% | Grade: A
All test assertions passed!`,
    topics: [
      "Defining functions with declarations and arrow syntax",
      "Positional parameters, default arguments, and return values",
      "Variable scope (local block scope vs global namespace)",
      "Writing reusable pure functions"
    ],
    tutorFocus: "Tutor reviews code structure to eliminate copy-pasted blocks and refactors logic into clean, testable functions."
  },
  {
    level: 5,
    grade: "Arrays and Objects",
    phase: "Core Skills",
    duration: "Core Skills (4 Sessions)",
    status: "available",
    description: "Group related values and look them up quickly when a program needs more than one piece of data.",
    milestone: {
      title: "Student Gradebook & Analytics Dashboard",
      description: "A data processing script that stores complex classroom records in arrays and objects, computing highest scorers, grade averages, and status rosters.",
      skills: ["Arrays & Objects", "Array Methods (map, reduce)", "Destructuring & Spread", "Data Aggregation"]
    },
    code: `// Level 5: Gradebook Analytics
const classroom = [
  { name: "Maya", scores: [92, 95, 98] },
  { name: "Leo", scores: [84, 88, 79] },
  { name: "Devon", scores: [90, 91, 94] }
];

for (const student of classroom) {
  const sum = student.scores.reduce((total, score) => total + score, 0);
  student.average = Number((sum / student.scores.length).toFixed(1));
}

// Find top performing student
const topStudent = [...classroom].sort((a, b) => b.average - a.average)[0];
console.log(\`Top Performer: \${topStudent.name} (\${topStudent.average}%)\`);
console.log(\`Class Roster Processed: \${classroom.length} students.\`);`,
    terminalOutput: `$ node gradebook_analytics.js
Processing 3 student profiles...
- Maya: 95.0%
- Leo: 83.7%
- Devon: 91.7%

Top Performer: Maya (95.0%)
Class Roster Processed: 3 students.`,
    topics: [
      "Arrays, object literals, and common collection methods",
      "Transforming collections with map(), filter(), and reduce()",
      "Accessing nested data structures and object destructuring",
      "JSON data representation and array iteration"
    ],
    tutorFocus: "Tutor works through realistic multi-row datasets and teaches how to manipulate nested object arrays without mutating source data."
  },
  {
    level: 6,
    grade: "The DOM and Events",
    phase: "Applied JavaScript",
    duration: "Applied JavaScript (4 Sessions)",
    status: "available",
    description: "Connect JavaScript to the page so programs respond to clicks, typing, and visible changes in the interface.",
    milestone: {
      title: "Interactive Task Kanban with Live DOM Updates",
      description: "A responsive client-side task board featuring dynamic element creation, event delegation, active filter toggles, and instant UI state synchronization.",
      skills: ["querySelector & createElement", "addEventListener & Event Bubbling", "classList & Style Updates", "Event Delegation"]
    },
    code: `// Level 6: Interactive DOM Controller
const taskList = document.querySelector("#task-list");

function addTask(title) {
  const item = document.createElement("li");
  item.className = "task-item";
  item.innerHTML = \`
    <span class="task-label">\${title}</span>
    <button class="delete-btn">Remove</button>
  \`;
  taskList.appendChild(item);
}

// Event delegation pattern
taskList.addEventListener("click", (e) => {
  if (e.target.classList.contains("delete-btn")) {
    e.target.closest(".task-item").remove();
  } else if (e.target.classList.contains("task-label")) {
    e.target.classList.toggle("completed");
  }
});

addTask("Review JavaScript Event Loop");
console.log("DOM elements rendered and listeners attached.");`,
    terminalOutput: `$ open index.html (Browser Console)
[DOM Initialized] Mounted #task-list to document body.
> addTask("Review JavaScript Event Loop") -> DOM element appended.
> User Click Event: <button class="delete-btn"> intercepted.
> Event delegation resolved target: 1 item removed.
Active list count updated: DOM state synced!`,
    topics: [
      "Selecting elements with querySelector and querySelectorAll",
      "Modifying text, HTML attributes, and CSS class lists",
      "Listening to user events: click, input, and keydown",
      "Event bubbling, propagation, and event delegation patterns"
    ],
    tutorFocus: "Tutor demonstrates how the DOM tree works behind the scenes, ensuring the learner understands event bubbling and cleanly manages UI state."
  },
  {
    level: 7,
    grade: "Asynchronous JavaScript",
    phase: "Applied JavaScript",
    duration: "Applied JavaScript (4 Sessions)",
    status: "available",
    description: "Work with time and remote data so a program can fetch information, recover from errors, and keep the page responsive.",
    milestone: {
      title: "Live Currency & Weather Data Fetcher (Async/Await)",
      description: "An asynchronous data client that queries external REST APIs, handles network latency with loading states, parses JSON, and catches network errors gracefully.",
      skills: ["Promises & Async / Await", "Fetch API & Response Handling", "try / catch / finally", "Loading & Error UI States"]
    },
    code: `// Level 7: Asynchronous API Data Client
async function fetchExchangeRate(base = "USD", target = "EUR") {
  const url = \`https://api.rflearn.dev/v1/forex?base=\${base}&target=\${target}\`;
  
  try {
    console.log(\`Fetching latest exchange rates for \${base}/\${target}...\`);
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(\`Server returned HTTP \${response.status}\`);
    }
    
    const data = await response.json();
    return { rate: data.rate, updated: data.timestamp };
  } catch (error) {
    console.error("Forex lookup failed:", error.message);
    return null;
  }
}

const forex = await fetchExchangeRate("USD", "EUR");
console.log(\`1 USD = \${forex.rate} EUR (Verified live at \${forex.updated})\`);`,
    terminalOutput: `$ node forex_fetcher.js
Fetching latest exchange rates for USD/EUR...
Connected to https://api.rflearn.dev (HTTP 200 OK)
1 USD = 0.92 EUR (Verified live at 14:02:10 UTC)
Network error boundaries and JSON parsing verified!`,
    topics: [
      "The JavaScript Event Loop, Call Stack, and Task Queue",
      "Promises: states (pending, fulfilled, rejected) and chaining",
      "Modern asynchronous code with async and await",
      "Fetching remote JSON APIs and defensive error handling"
    ],
    tutorFocus: "Tutor uses visual event-loop simulators to show why JavaScript does not freeze the screen while waiting for network responses."
  },
  {
    level: 8,
    grade: "Projects and Problem Solving",
    phase: "Capstone",
    duration: "Capstone (4-6 Sessions)",
    status: "available",
    description: "Put the pieces together on a project that reads input, updates the page, and produces a complete, portfolio-ready result.",
    milestone: {
      title: "Modular Productivity & Habit App with LocalStorage",
      description: "A comprehensive production web application featuring an architectural controller, localStorage state persistence, modular components, automated unit tests, and defense.",
      skills: ["ES6 Modules (import/export)", "LocalStorage Persistence", "Defensive Architecture", "Portfolio Presentation"]
    },
    code: `// Level 8 Capstone: Modular Habit App Architecture
class HabitTracker {
  constructor(storageKey = "rflearn_habits") {
    this.key = storageKey;
    this.habits = this.load();
  }

  load() {
    try {
      const stored = localStorage.getItem(this.key);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  add(name) {
    const habit = { id: Date.now(), name, done: false };
    this.habits.push(habit);
    localStorage.setItem(this.key, JSON.stringify(this.habits));
    return habit;
  }
}

console.log("rfLearn JavaScript Capstone v1.0 compiled successfully.");`,
    terminalOutput: `$ npm test
Running test suite: HabitTracker.test.js...
✓ Initializes clean local storage store
✓ Successfully adds new habit item with timestamp ID
✓ Toggles streak counters and persists to disk
✓ Defensive fallback on corrupted JSON payload

Ran 4 tests in 0.051s - OK
Capstone defense verified for student portfolio!`,
    topics: [
      "System architecture, MVC patterns, and multi-file code",
      "ES6 Modules: imports, exports, and encapsulation",
      "Data persistence with LocalStorage and SessionStorage",
      "Publishing code to GitHub and hosting live web demos"
    ],
    tutorFocus: "Tutor conducts formal code reviews, teaches git basics, and prepares the student to confidently explain their JavaScript project in college or job interviews."
  }
];

const JavaScriptCourse: React.FC = () => {
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const [activeConsoleTab, setActiveConsoleTab] = useState<"code" | "output">("code");

  const getAvailableModulesCount = () => {
    return javascriptCurriculum.filter(module => module.status === "available").length;
  };

  const currentModule = javascriptCurriculum[selectedModuleIndex];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow overflow-hidden py-8 sm:py-12">
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <div className="mb-20 grid items-center gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-20">
              <div className="lg:py-8">
                <h1 className="max-w-[14ch] text-5xl font-extrabold leading-[0.98] tracking-[-0.04em] text-gray-950 sm:text-6xl lg:text-7xl">
                  Learn to think in JavaScript.
                </h1>
                <p className="mt-7 max-w-xl text-lg leading-8 text-gray-600 sm:text-xl">
                  A computer science path that starts in the browser, then builds toward interactive projects—one skill at a time.
                </p>
                <p className="mt-5 max-w-lg text-base leading-7 text-gray-500">
                  Our {getAvailableModulesCount()}-module JavaScript sequence covers the language, the page, and the habits learners need to keep going.
                </p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" className="button-gradient text-white" asChild>
                    <Link to={ROUTES.REGISTER.path}>
                      Start learning JavaScript
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-gray-300 bg-white text-gray-800 hover:bg-rfpurple-50 hover:text-rfpurple-800"
                    asChild
                  >
                    <Link to={ROUTES.BOOK_DEMO.path}>
                      <Users className="mr-2 h-5 w-5" />
                      Find the right fit
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="relative border-y border-rfpurple-200 bg-rfpurple-50 px-6 py-7 sm:px-8 sm:py-9">
                <div className="absolute -left-px top-8 bottom-8 w-1 bg-rfpurple-600" aria-hidden="true" />
                <p className="pl-4 text-sm font-semibold text-rfpurple-800">A course path with room to grow</p>
                <div className="mt-7 divide-y divide-rfpurple-200">
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5 first:pt-0">
                    <span className="text-sm font-bold text-rfpurple-700">Start</span>
                    <div>
                      <p className="font-bold text-gray-900">Write working scripts</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">Setup, values, the console, and the first programs that run in the browser.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 py-5">
                    <span className="text-sm font-bold text-rfpurple-700">Build</span>
                    <div>
                      <p className="font-bold text-gray-900">Control the logic</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">Conditions, loops, functions, arrays, and objects that scale beyond one file.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-[5.5rem_1fr] gap-4 pb-0 pt-5">
                    <span className="text-sm font-bold text-rfpurple-700">Apply</span>
                    <div>
                      <p className="font-bold text-gray-900">Make the page respond</p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">The DOM, events, fetching data, and a finished project you can explain.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Journey Section (Interactive Studio Explorer with Milestone Builds) */}
            <div className="mb-20">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3 sm:text-4xl">
                  JavaScript Course Journey
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Select any level to inspect key concepts, explore the JavaScript code, and see the exact Milestone Build learners produce with their tutor.
                </p>

                {/* Phase Pills */}
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Phase 1: Foundations (L1-2)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-blue-500" /> Phase 2: Core Skills (L3-5)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-rfpurple-500" /> Phase 3: Applied JavaScript (L6-7)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-orange-500" /> Phase 4: Capstone (L8)
                  </span>
                </div>
              </div>

              {/* Split-Pane Studio Explorer Container */}
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* Left Module Rail (5 cols) */}
                  <div className="lg:col-span-5 space-y-3">
                    <div className="flex items-center justify-between px-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                      <span>Course Track</span>
                      <span>8 Modules</span>
                    </div>

                    <div className="space-y-2.5 max-h-[680px] overflow-y-auto pr-1">
                      {javascriptCurriculum.map((mod, idx) => {
                        const isSelected = idx === selectedModuleIndex;
                        return (
                          <button
                            key={mod.level}
                            type="button"
                            onClick={() => {
                              setSelectedModuleIndex(idx);
                              setActiveConsoleTab("code");
                            }}
                            className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-start justify-between gap-3 ${
                              isSelected
                                ? "bg-rfpurple-50 border-rfpurple-500 shadow-sm ring-1 ring-rfpurple-400"
                                : "bg-white hover:bg-slate-50 border-slate-200"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`w-6 h-6 mt-0.5 rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? "bg-rfpurple-600 text-white" : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {mod.level}
                              </span>
                              <div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-xs font-bold ${isSelected ? "text-rfpurple-950" : "text-slate-900"}`}>
                                    {mod.grade}
                                  </span>
                                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                    {mod.phase}
                                  </span>
                                </div>
                                <div
                                  className={`mt-1 flex items-center gap-1 text-[11px] font-semibold ${
                                    isSelected ? "text-orange-700" : "text-slate-500"
                                  }`}
                                >
                                  <Wrench className="w-3 h-3 text-orange-500 flex-shrink-0" />
                                  <span className="truncate max-w-[210px]">{mod.milestone.title}</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight
                              className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform ${
                                isSelected ? "text-rfpurple-600 translate-x-0.5" : "text-slate-300"
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>

                    <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl text-center">
                      <p className="text-xs text-slate-600">Unsure where your learner starts?</p>
                      <Link
                        to={ROUTES.BOOK_DEMO.path}
                        className="text-xs font-bold text-rfpurple-700 hover:text-rfpurple-800 underline mt-0.5 inline-block"
                      >
                        Schedule a 1-on-1 placement diagnostic →
                      </Link>
                    </div>
                  </div>

                  {/* Right Stage Detail (7 cols) */}
                  <div className="lg:col-span-7 space-y-5">
                    <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-sm relative">
                      
                      {/* Top Header & Badges */}
                      <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-slate-100 mb-4">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-md text-xs font-mono font-bold bg-rfpurple-100 text-rfpurple-800 border border-rfpurple-200">
                            LEVEL {currentModule.level}
                          </span>
                          <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">
                            {currentModule.phase}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> Available for 1-on-1 Enrollment
                        </span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-2xl font-extrabold text-slate-900 mb-2">
                        {currentModule.grade}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-5">
                        {currentModule.description}
                      </p>

                      {/* Milestone Build Box (Peach Background) */}
                      <div className="p-4 rounded-xl bg-gradient-to-r from-orange-50/90 via-amber-50/40 to-white border-2 border-orange-200/90 mb-5 relative overflow-hidden shadow-xs">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-lg bg-orange-500 text-white flex items-center justify-center text-xs font-bold shadow-xs flex-shrink-0">
                            <Wrench className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-xs font-extrabold uppercase tracking-wider text-orange-900 flex-shrink-0">
                            Milestone Build:
                          </span>
                          <span className="text-sm font-bold text-slate-900">
                            {currentModule.milestone.title}
                          </span>
                        </div>

                        <p className="text-xs text-slate-700 leading-relaxed mb-3">
                          {currentModule.milestone.description}
                        </p>

                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-orange-200/60 text-[11px] text-orange-950 font-medium">
                          {currentModule.milestone.skills.map((skill) => (
                            <span
                              key={skill}
                              className="bg-white/80 px-2 py-0.5 rounded border border-orange-200"
                            >
                              ✓ {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Dual-Tab Code / Terminal Console */}
                      <div className="mb-5 bg-slate-950 rounded-xl border border-slate-800 overflow-hidden shadow-lg">
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-900 border-b border-slate-800">
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setActiveConsoleTab("code")}
                              className={`px-3 py-1 text-xs font-mono font-semibold rounded transition-all flex items-center gap-1.5 ${
                                activeConsoleTab === "code"
                                  ? "bg-slate-800 text-white border border-slate-700"
                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                              }`}
                            >
                              <Code2 className="w-3.5 h-3.5 text-rfpurple-400" />
                              main.js
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveConsoleTab("output")}
                              className={`px-3 py-1 text-xs font-mono font-semibold rounded transition-all flex items-center gap-1.5 ${
                                activeConsoleTab === "output"
                                  ? "bg-slate-800 text-emerald-300 border border-slate-700"
                                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                              }`}
                            >
                              <Terminal className="w-3.5 h-3.5 text-emerald-400" />
                              Milestone Run ($ node)
                            </button>
                          </div>
                          <span className="text-[11px] font-mono text-slate-500 hidden sm:inline">
                            Node.js / ES2024
                          </span>
                        </div>

                        {activeConsoleTab === "code" ? (
                          <div className="p-4 font-mono text-xs leading-relaxed text-white overflow-x-auto">
                            <pre className="whitespace-pre text-white">{currentModule.code}</pre>
                          </div>
                        ) : (
                          <div className="p-4 font-mono text-xs leading-relaxed text-emerald-300 bg-black/95 overflow-x-auto">
                            <pre className="whitespace-pre">{currentModule.terminalOutput}</pre>
                          </div>
                        )}
                      </div>

                      {/* Core Learning Topics */}
                      <div className="mb-5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">
                          Core Competencies Mastered
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {currentModule.topics.map((topic) => (
                            <div
                              key={topic}
                              className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-700"
                            >
                              <span className="text-rfpurple-600 font-bold mt-0.5">•</span>
                              <span>{topic}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 1-on-1 Tutor Value Prop */}
                      <div className="p-3.5 bg-rfpurple-50/70 border border-rfpurple-200 rounded-xl mb-6">
                        <div className="flex items-center gap-2 text-xs font-bold text-rfpurple-900 mb-1">
                          <Sparkles className="w-4 h-4 text-rfpurple-600" />
                          <span>How 1-on-1 Tutors Support this Milestone</span>
                        </div>
                        <p className="text-xs text-rfpurple-950/80 leading-relaxed">
                          {currentModule.tutorFocus}
                        </p>
                      </div>

                      {/* Action Footer */}
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
                        <div className="text-xs text-slate-500">
                          Pacing: <strong className="text-slate-800">{currentModule.duration}</strong>
                        </div>
                        <Button size="sm" className="button-gradient text-white w-full sm:w-auto" asChild>
                          <Link to={ROUTES.REGISTER.path}>
                            Enroll in Level {currentModule.level}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>

                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Bottom CTA Banner */}
            <div className="bg-gradient-to-r from-rfpurple-600 to-rfpurple-500 rounded-2xl p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Start Coding?</h2>
              <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                Begin with JavaScript and learn computer science with expert tutors and interactive practice.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="bg-white text-rfpurple-600 hover:bg-gray-100" asChild>
                  <Link to={ROUTES.REGISTER.path}>
                    <Target className="mr-2 h-5 w-5" />
                    Get Started Now
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-rfpurple-600 bg-transparent" asChild>
                  <Link to={ROUTES.INQUIRY.path}>
                    <BookOpen className="mr-2 h-5 w-5" />
                    Learn More
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JavaScriptCourse;
